import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Package } from 'lucide-react';
import { listRecords } from '../lib/supabase';

const getFirstImage = (product) => {
    if (product.imageUrls) {
        try {
            const urls = typeof product.imageUrls === 'string'
                ? JSON.parse(product.imageUrls)
                : product.imageUrls;
            if (Array.isArray(urls) && urls.length) return urls[0];
        } catch (error) {
            console.error('Error parsing imageUrls:', error);
        }
    }
    return product.imageUrl || null;
};

const FeaturedMarketplace = () => {
    const [products, setProducts] = useState([]);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const response = { documents: await listRecords('marketplace') };

                // Get all products and shuffle them
                const allProducts = response.documents;
                const shuffled = [...allProducts].sort(() => 0.5 - Math.random());

                // Take the first 3 products
                setProducts(shuffled.slice(0, 3));
            } catch (error) {
                console.error('Error fetching featured products:', error);
                setError('Failed to load featured products');
            }
        };

        fetchProducts();
    }, []);

    if (error || products.length === 0) {
        return null; // Don't show the section on error or when empty
    }

    return (
        <div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-5">
                {products.map(product => {
                    const image = getFirstImage(product);
                    return (
                        <Link
                            key={product.$id}
                            to="/marketplace"
                            className="group flex flex-col overflow-hidden rounded-xl border border-gray-100 bg-white
                                     shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-purple-100 hover:shadow-lg"
                        >
                            <div className="relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-purple-100 via-purple-50 to-gray-100">
                                {image ? (
                                    <img
                                        src={image}
                                        alt={product.name}
                                        className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
                                    />
                                ) : (
                                    <div className="grid h-full place-items-center">
                                        <Package className="h-8 w-8 text-purple-300" />
                                    </div>
                                )}
                                {product.category && (
                                    <span className="absolute left-2 top-2 rounded-full bg-white/90 px-2 py-0.5 text-[10px]
                                                   font-semibold capitalize text-purple-700 shadow-sm backdrop-blur">
                                        {product.category}
                                    </span>
                                )}
                            </div>
                            <div className="flex flex-1 flex-col p-3">
                                <h3 className="line-clamp-1 font-display text-sm font-semibold text-gray-900
                                             transition-colors group-hover:text-purple-700">
                                    {product.name}
                                </h3>
                                {product.price ? (
                                    <p className="mt-1 text-base font-bold text-purple-700">${product.price}</p>
                                ) : (
                                    <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-gray-400">Enquire</p>
                                )}
                            </div>
                        </Link>
                    );
                })}
            </div>
            <div className="mt-6 text-center sm:mt-8">
                <Link
                    to="/marketplace"
                    className="group inline-flex items-center rounded-full bg-purple-600 px-6 py-3 text-base font-semibold
                             text-white shadow-sm transition-all hover:-translate-y-0.5 hover:bg-purple-700 hover:shadow-md"
                >
                    View All Items
                    <ArrowRight className="ml-2 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
            </div>
        </div>
    );
};

export default FeaturedMarketplace;
